import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { http } from '@/plugins/axios'
import { translate } from '@/i18n'
import { useUserStore } from '@/stores/user.store'
import { apiEndpoints, buildApiPath } from '@/shared/constants/api'
import type { AuthRole, AuthSession, AuthTenant, AuthUser } from '@/shared/types/auth.type'
import type { ApiResponseEnvelope } from '@/shared/types/api.type'
import type { Branch } from '@/shared/types/branch.type'
import type { UserProfile } from '@/shared/types/user.type'
import type {
  UpdateUserProfilePayload,
  UserAvatarPresignPayload,
  UserAvatarPresignResult,
  UserGender,
} from '@/shared/types/user.type'

interface LoginPayload {
  tenant_code: string
  username: string
  password: string
  remember: boolean
}

interface ForgotPasswordPayload {
  tenant_code: string
  username: string
}

interface LoginApiUser {
  id?: number
  name?: string | null
  full_name?: string | null
  username?: string | null
  employee_code?: string | null
  email?: string | null
  phone?: string | null
  avatar?: string | null
  gender?: UserGender | null
  birthday?: string | null
  address?: string | null
  points_balance?: number | null
  status?: string | null
  last_login_at?: string | null
  must_change_password?: boolean | null
}

interface LoginApiTenant {
  code?: string | null
  name?: string | null
  domain?: string | null
  subdomain?: string | null
}

interface LoginApiRole {
  id?: number
  name?: string | null
  code?: string | null
}

interface AuthApiData {
  access_token?: string
  token?: string
  refresh_token?: string
  user?: LoginApiUser
  staff?: LoginApiUser
  tenant?: LoginApiTenant
  roles?: LoginApiRole[]
  permissions?: string[]
  branches?: Branch[]
  current_branch?: Branch | null
  default_branch_id?: number | null
}

interface SwitchBranchApiData {
  current_branch?: Branch | null
  roles?: LoginApiRole[]
  permissions?: string[]
}

interface LoginApiPayload {
  tenant_code: string
  username: string
  login: string
  password: string
}

interface ForgotPasswordApiPayload {
  tenant_code: string
  username: string
  login: string
}

interface LogoutApiPayload {
  refresh_token?: string
}

interface ChangePasswordPayload {
  current_password: string
  password: string
  password_confirmation: string
}

const storageKey = 'bigsale.auth.session'

function isAuthSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== 'object') {
    return false
  }

  const session = value as Partial<AuthSession>
  const accessToken = session.access_token ?? session.token

  return (
    typeof accessToken === 'string' &&
    typeof session.user?.id === 'number' &&
    typeof session.user?.name === 'string' &&
    typeof session.user?.username === 'string' &&
    typeof session.tenant?.code === 'string' &&
    typeof session.tenant?.name === 'string' &&
    typeof session.tenant?.domain === 'string'
  )
}

function normalizeAuthSession(session: AuthSession): AuthSession {
  const accessToken = session.access_token ?? session.token
  const branches = session.branches ?? []
  const currentBranch =
    session.current_branch ??
    branches.find((branch) => branch.id === session.currentBranchId) ??
    (branches.length === 1 ? branches[0] : null)

  return {
    ...session,
    token: accessToken,
    access_token: accessToken,
    branches,
    current_branch: currentBranch,
    currentBranchId: session.currentBranchId ?? currentBranch?.id ?? null,
  }
}

function saveSession(session: AuthSession) {
  // Auth sessions must be shared by every tab on the same origin. sessionStorage
  // is isolated per tab, so a newly opened tab would incorrectly appear logged out.
  localStorage.setItem(storageKey, JSON.stringify(session))
  sessionStorage.removeItem(storageKey)
}

function getCurrentSessionStorage(): Storage | null {
  if (localStorage.getItem(storageKey) !== null) {
    return localStorage
  }

  return null
}

function clearStoredSession() {
  localStorage.removeItem(storageKey)
  sessionStorage.removeItem(storageKey)
}

function saveSessionToCurrentStorage(session: AuthSession): boolean {
  const storage = getCurrentSessionStorage()

  if (!storage) {
    return false
  }

  storage.setItem(storageKey, JSON.stringify(session))
  return true
}

function mapUser(user: LoginApiUser | undefined, fallbackUsername: string): AuthUser {
  return {
    id: Number(user?.id ?? 0),
    name: user?.name ?? user?.full_name ?? user?.username ?? fallbackUsername,
    username: user?.username ?? user?.email ?? user?.phone ?? fallbackUsername,
    employee_code: user?.employee_code ?? null,
    email: user?.email ?? null,
    phone: user?.phone ?? null,
    avatar: user?.avatar ?? null,
    gender: user?.gender ?? null,
    birthday: user?.birthday ?? null,
    address: user?.address ?? null,
    points_balance: Number(user?.points_balance ?? 0),
    status: user?.status ?? null,
    last_login_at: user?.last_login_at ?? null,
    must_change_password: Boolean(user?.must_change_password),
  }
}

function mapTenant(tenant: LoginApiTenant | undefined, fallbackCode: string): AuthTenant {
  const code = tenant?.code ?? fallbackCode
  const domain =
    tenant?.domain ?? (tenant?.subdomain ? `${tenant.subdomain}.bigsale.vn` : `${code}.bigsale.vn`)

  return {
    code,
    name: tenant?.name ?? code.toUpperCase(),
    domain,
  }
}

function mapRoles(roles: LoginApiRole[] | undefined): AuthRole[] {
  return (roles ?? []).map((role) => ({
    id: Number(role.id ?? 0),
    name: role.name ?? '',
    code: role.code ?? '',
  }))
}

function mapBranchContext(data: AuthApiData, fallback?: AuthSession) {
  const branches = data.branches ?? fallback?.branches ?? []
  const defaultBranchId = data.default_branch_id ?? fallback?.currentBranchId ?? null
  const currentBranch =
    data.current_branch ??
    branches.find((branch) => branch.id === defaultBranchId) ??
    (branches.length === 1 ? branches[0] : (fallback?.current_branch ?? null))
  const currentBranchId = currentBranch?.id ?? defaultBranchId

  return {
    branches,
    current_branch: currentBranch,
    currentBranchId: currentBranchId ?? null,
  }
}

function createAuthSession(
  data: AuthApiData,
  payload: Pick<LoginPayload, 'tenant_code' | 'username'>,
): AuthSession {
  const accessToken = data.access_token ?? data.token

  if (!accessToken) {
    throw new Error('Login response is missing access token.')
  }

  const tenantCode = payload.tenant_code.trim().toLowerCase()

  return {
    token: accessToken,
    access_token: accessToken,
    refresh_token: data.refresh_token,
    user: mapUser(data.user ?? data.staff, payload.username),
    tenant: mapTenant(data.tenant, tenantCode),
    roles: mapRoles(data.roles),
    permissions: data.permissions ?? [],
    ...mapBranchContext(data),
  }
}

function mergeStoredSessionWithApiData(storedSession: AuthSession, data: AuthApiData): AuthSession {
  const latestStoredSession = readStoredAuthSession() ?? storedSession
  const accessToken = data.access_token ?? data.token ?? latestStoredSession.access_token

  return {
    ...latestStoredSession,
    token: accessToken,
    access_token: accessToken,
    refresh_token: data.refresh_token ?? latestStoredSession.refresh_token,
    user: mapUser(data.user ?? data.staff, latestStoredSession.user.username),
    tenant: mapTenant(data.tenant, latestStoredSession.tenant.code),
    roles: mapRoles(data.roles),
    permissions: data.permissions ?? latestStoredSession.permissions ?? [],
    ...mapBranchContext(data, latestStoredSession),
  }
}

function mapSessionToUserProfile(session: AuthSession): UserProfile {
  return {
    ...session.user,
    roles: session.roles ?? [],
    permissions: session.permissions ?? [],
  }
}

function getLoginErrorMessage(errorValue: unknown) {
  return errorValue instanceof Error ? errorValue.message : 'Login failed.'
}

export function readStoredAuthSession(): AuthSession | null {
  const localSession = localStorage.getItem(storageKey)
  const legacyTabSession = sessionStorage.getItem(storageKey)
  const rawSession = localSession ?? legacyTabSession

  if (!rawSession) {
    return null
  }

  try {
    const parsedSession = JSON.parse(rawSession)

    if (isAuthSession(parsedSession)) {
      const normalizedSession = normalizeAuthSession(parsedSession)

      if (localSession === null && legacyTabSession !== null) {
        localStorage.setItem(storageKey, JSON.stringify(normalizedSession))
        sessionStorage.removeItem(storageKey)
      }

      return normalizedSession
    }

    localStorage.removeItem(storageKey)
    sessionStorage.removeItem(storageKey)
    return null
  } catch {
    clearStoredSession()
    return null
  }
}

export interface AuthTokenUpdate {
  access_token: string
  refresh_token?: string
}

export interface RestoreSessionOptions {
  allowUnavailable?: (storedSession: AuthSession, error: unknown) => boolean | Promise<boolean>
}

function isUnavailableAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const candidate = error as { isNetworkError?: unknown; isTimeoutError?: unknown }

  return candidate.isNetworkError === true || candidate.isTimeoutError === true
}

export function updateStoredAuthTokens(tokens: AuthTokenUpdate): AuthSession | null {
  const session = readStoredAuthSession()

  if (!session) {
    return null
  }

  const nextSession: AuthSession = {
    ...session,
    token: tokens.access_token,
    access_token: tokens.access_token,
    refresh_token: tokens.refresh_token ?? session.refresh_token,
  }
  const storage = localStorage.getItem(storageKey) !== null ? localStorage : null

  if (!storage) {
    return null
  }

  storage.setItem(storageKey, JSON.stringify(nextSession))
  window.dispatchEvent(new CustomEvent('bigsale.auth.tokens-refreshed', { detail: nextSession }))

  return nextSession
}

export const useAuthStore = defineStore('auth', () => {
  const storedSession = readStoredAuthSession()
  const userStore = useUserStore()
  const token = ref<string | null>(storedSession?.token ?? null)
  const user = ref<AuthUser | null>(storedSession?.user ?? null)
  const tenant = ref<AuthTenant | null>(storedSession?.tenant ?? null)
  const branches = ref<Branch[]>(storedSession?.branches ?? [])
  const currentBranch = ref<Branch | null>(storedSession?.current_branch ?? null)
  const currentBranchId = ref<number | null>(
    storedSession?.currentBranchId ?? currentBranch.value?.id ?? null,
  )
  const isSessionVerified = ref(false)
  const isLoading = ref(false)
  const isBootstrapping = ref(false)
  const error = ref<string | null>(null)
  const isUpdatingProfile = ref(false)
  const isUploadingAvatar = ref(false)
  const profileError = ref<string | null>(null)

  const isAuthenticated = computed(() => Boolean(token.value))

  function applySession(session: AuthSession | null) {
    token.value = session?.token ?? null
    user.value = session?.user ?? null
    tenant.value = session?.tenant ?? null
    branches.value = session?.branches ?? []
    currentBranch.value = session?.current_branch ?? null
    currentBranchId.value = session?.currentBranchId ?? currentBranch.value?.id ?? null

    if (session) {
      userStore.setProfile(mapSessionToUserProfile(session))
      return
    }

    userStore.clearProfile()
  }

  async function login(payload: LoginPayload) {
    isSessionVerified.value = false
    isLoading.value = true
    error.value = null

    try {
      const tenantCode = payload.tenant_code.trim().toLowerCase()
      const response = await http.post<ApiResponseEnvelope<AuthApiData>, LoginApiPayload>(
        buildApiPath(apiEndpoints.auth.login),
        {
          tenant_code: tenantCode,
          username: payload.username,
          login: payload.username,
          password: payload.password,
        },
        {
          skipAuth: true,
          errorNotificationMessage: translate('auth.login.actions.failed'),
          successNotificationMessage: translate('auth.login.actions.success'),
          headers: {
            'X-Tenant-Code': tenantCode,
          },
        },
      )

      if (!response.data) {
        throw new Error(response.message || 'Login response is empty.')
      }

      const session = createAuthSession(response.data, {
        ...payload,
        tenant_code: tenantCode,
      })

      applySession(session)
      saveSession(session)
      isSessionVerified.value = true

      return session
    } catch (exception) {
      error.value = getLoginErrorMessage(exception)
      throw exception
    } finally {
      isLoading.value = false
    }
  }

  async function restoreSessionFromApi(options: RestoreSessionOptions = {}): Promise<boolean> {
    isSessionVerified.value = false
    const storedSession = readStoredAuthSession()

    if (!storedSession) {
      applySession(null)
      return false
    }

    applySession(storedSession)
    isBootstrapping.value = true
    error.value = null

    try {
      const response = await http.get<ApiResponseEnvelope<AuthApiData>>(
        buildApiPath(apiEndpoints.auth.me),
      )

      if (!response.data) {
        throw new Error(response.message || 'User detail response is empty.')
      }

      const nextSession = mergeStoredSessionWithApiData(storedSession, response.data)

      applySession(nextSession)
      saveSessionToCurrentStorage(nextSession)
      isSessionVerified.value = true

      return true
    } catch (exception) {
      if (
        options.allowUnavailable &&
        isUnavailableAuthError(exception) &&
        (await options.allowUnavailable(storedSession, exception))
      ) {
        isSessionVerified.value = true
        return true
      }

      logout()
      return false
    } finally {
      isBootstrapping.value = false
    }
  }

  async function logoutFromApi(): Promise<void> {
    const storedSession = readStoredAuthSession()

    if (!storedSession) {
      logout()
      return
    }

    isLoading.value = true
    error.value = null

    try {
      await http.post<ApiResponseEnvelope<null>, LogoutApiPayload>(
        buildApiPath(apiEndpoints.auth.logout),
        storedSession.refresh_token ? { refresh_token: storedSession.refresh_token } : {},
      )
      logout()
    } catch (exception) {
      error.value = getLoginErrorMessage(exception)
      throw exception
    } finally {
      isLoading.value = false
    }
  }

  async function forgotPassword(payload: ForgotPasswordPayload): Promise<string> {
    isLoading.value = true
    error.value = null

    try {
      const tenantCode = payload.tenant_code.trim().toLowerCase()
      const username = payload.username.trim()
      const response = await http.post<ApiResponseEnvelope<null>, ForgotPasswordApiPayload>(
        buildApiPath(apiEndpoints.auth.forgotPassword),
        {
          tenant_code: tenantCode,
          username,
          login: username,
        },
        {
          skipAuth: true,
          skipSuccessNotification: true,
          skipErrorNotification: true,
          headers: {
            'X-Tenant-Code': tenantCode,
          },
        },
      )

      return response.message || translate('auth.forgotPassword.feedback.success')
    } catch (exception) {
      error.value = getLoginErrorMessage(exception)
      throw exception
    } finally {
      isLoading.value = false
    }
  }

  async function changePassword(payload: ChangePasswordPayload): Promise<AuthSession> {
    isLoading.value = true
    error.value = null

    try {
      const storedSession = readStoredAuthSession()

      if (!storedSession) {
        throw new Error('Auth session is missing.')
      }

      const response = await http.post<ApiResponseEnvelope<AuthApiData>, ChangePasswordPayload>(
        buildApiPath(apiEndpoints.auth.changePassword),
        payload,
        { skipNotification: true },
      )

      if (!response.data) {
        throw new Error(response.message || 'Change password response is empty.')
      }

      const nextSession = mergeStoredSessionWithApiData(storedSession, response.data)

      applySession(nextSession)
      saveSessionToCurrentStorage(nextSession)

      return nextSession
    } catch (exception) {
      error.value = getLoginErrorMessage(exception)
      throw exception
    } finally {
      isLoading.value = false
    }
  }

  async function updateProfile(payload: UpdateUserProfilePayload): Promise<AuthSession> {
    isUpdatingProfile.value = true
    profileError.value = null

    try {
      const storedSession = readStoredAuthSession()

      if (!storedSession) {
        throw new Error(translate('auth.profile.errors.sessionMissing'))
      }

      const response = await http.patch<ApiResponseEnvelope<AuthApiData>, UpdateUserProfilePayload>(
        buildApiPath(apiEndpoints.auth.updateProfile),
        payload,
      )

      if (!response.data) {
        throw new Error(response.message || translate('auth.profile.errors.updateFailed'))
      }

      return applyProfileResponse(storedSession, response.data)
    } catch (exception) {
      profileError.value = getProfileErrorMessage(exception)
      throw exception
    } finally {
      isUpdatingProfile.value = false
    }
  }

  async function uploadAvatar(file: File): Promise<AuthSession> {
    isUploadingAvatar.value = true
    profileError.value = null

    try {
      const uploadResponse = await http.post<
        ApiResponseEnvelope<UserAvatarPresignResult>,
        UserAvatarPresignPayload
      >(buildApiPath(apiEndpoints.auth.avatarPresign), {
        filename: file.name,
        mime_type: file.type,
        size: file.size,
      })
      const upload = uploadResponse.data

      if (!upload) {
        throw new Error(translate('auth.profile.errors.avatarUploadFailed'))
      }

      const objectUploadResponse = await fetch(upload.upload_url, {
        method: 'PUT',
        headers: upload.headers,
        body: file,
      })

      if (!objectUploadResponse.ok) {
        throw new Error(translate('auth.profile.errors.avatarUploadFailed'))
      }

      return await confirmAvatar(upload.object_key)
    } catch (exception) {
      profileError.value = getProfileErrorMessage(exception)
      throw exception
    } finally {
      isUploadingAvatar.value = false
    }
  }

  async function deleteAvatar(): Promise<AuthSession> {
    isUploadingAvatar.value = true
    profileError.value = null

    try {
      const storedSession = readStoredAuthSession()

      if (!storedSession) {
        throw new Error(translate('auth.profile.errors.sessionMissing'))
      }

      const response = await http.delete<ApiResponseEnvelope<AuthApiData>>(
        buildApiPath(apiEndpoints.auth.avatarDelete),
      )

      if (!response.data) {
        throw new Error(response.message || translate('auth.profile.errors.avatarDeleteFailed'))
      }

      return applyProfileResponse(storedSession, response.data)
    } catch (exception) {
      profileError.value = getProfileErrorMessage(exception)
      throw exception
    } finally {
      isUploadingAvatar.value = false
    }
  }

  async function confirmAvatar(objectKey: string): Promise<AuthSession> {
    const storedSession = readStoredAuthSession()

    if (!storedSession) {
      throw new Error(translate('auth.profile.errors.sessionMissing'))
    }

    const response = await http.post<ApiResponseEnvelope<AuthApiData>, { object_key: string }>(
      buildApiPath(apiEndpoints.auth.avatarConfirm),
      { object_key: objectKey },
    )

    if (!response.data) {
      throw new Error(response.message || translate('auth.profile.errors.avatarUploadFailed'))
    }

    return applyProfileResponse(storedSession, response.data)
  }

  function applyProfileResponse(storedSession: AuthSession, data: AuthApiData): AuthSession {
    const nextSession = mergeStoredSessionWithApiData(storedSession, data)

    applySession(nextSession)
    saveSessionToCurrentStorage(nextSession)

    return nextSession
  }

  function getProfileErrorMessage(exception: unknown) {
    return exception instanceof Error && exception.message
      ? exception.message
      : translate('auth.profile.errors.updateFailed')
  }

  async function switchBranch(branchId: number) {
    const assignedBranch = branches.value.find((branch) => branch.id === branchId) ?? null

    if (!assignedBranch) {
      throw new Error('Branch is not assigned to current user.')
    }

    const response = await http.post<
      ApiResponseEnvelope<SwitchBranchApiData>,
      { branch_id: number }
    >(buildApiPath(apiEndpoints.branch.current), { branch_id: branchId })
    const nextBranch = response.data?.current_branch ?? assignedBranch

    currentBranch.value = nextBranch
    currentBranchId.value = nextBranch.id

    const storedSession = readStoredAuthSession()
    const nextSession = storedSession
      ? {
          ...storedSession,
          branches: branches.value,
          current_branch: nextBranch,
          currentBranchId: nextBranch.id,
          roles: response.data?.roles ? mapRoles(response.data.roles) : storedSession.roles,
          permissions: response.data?.permissions ?? storedSession.permissions ?? [],
        }
      : null

    if (nextSession) {
      userStore.setProfile(mapSessionToUserProfile(nextSession))
      saveSessionToCurrentStorage(nextSession)
    }

    return nextBranch
  }

  function logout() {
    isSessionVerified.value = false
    applySession(null)
    error.value = null
    clearStoredSession()
  }

  if (storedSession) {
    userStore.setProfile(mapSessionToUserProfile(storedSession))
  }

  return {
    token,
    user,
    tenant,
    branches,
    currentBranch,
    currentBranchId,
    isAuthenticated,
    isSessionVerified,
    isLoading,
    isBootstrapping,
    error,
    isUpdatingProfile,
    isUploadingAvatar,
    profileError,
    login,
    restoreSessionFromApi,
    switchBranch,
    forgotPassword,
    changePassword,
    updateProfile,
    uploadAvatar,
    deleteAvatar,
    logoutFromApi,
    logout,
  }
})
